import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Numeric } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import { SegmentControl } from '@root/shared/ui-kit'

import { TradingInputSlider } from '../trading-input-slider'

import type { SellControlsModel } from '../../../atoms/common'
import type { LimitOrder } from '@root/entities/limit-order'
import type { Pair } from '@root/entities/pair'

type Props = {
  pair: Pair.PairMinimal
  model: SellControlsModel['limitModel']
}

export const TradingControlsLimitSell: React.FC<Props> = ({
  pair: _,
  model,
}) => {
  const { t } = useTranslation()
  const {
    targetTypeAtom,
    sellLowPercentsAtom,
    sellHighPercentsAtom,
    sellHighUsdAtom,
    sellLowUsdAtom,
  } = model

  const [targetType, setTargetType] = useAtom(targetTypeAtom)

  const [, setHighPercentsRaw] = useAtom(sellHighPercentsAtom.raw)
  const [highPercents] = useAtom(sellHighPercentsAtom)
  const [, setLowPercentsRaw] = useAtom(sellLowPercentsAtom.raw)
  const [lowPercents] = useAtom(sellLowPercentsAtom)

  const [highUsd] = useAtom(sellHighUsdAtom)
  const [lowUsd] = useAtom(sellLowUsdAtom)

  return (
    <Root>
      <SegmentControl value={targetType} onChange={setTargetType}>
        <Note>{t('trading.limit-rate-by')}</Note>
        <SegmentControl.Item
          value={'market-cap' satisfies LimitOrder.TargetType}
        >
          {t('market-cap')}
        </SegmentControl.Item>
        <SegmentControl.Item value={'absolute' satisfies LimitOrder.TargetType}>
          {t('common.price')}
        </SegmentControl.Item>
      </SegmentControl>

      <TradingInputSlider
        type="sell"
        themeType="buy"
        value={highPercents.toNumber()}
        ticks={[0, 25, 50, 75, 100]}
        forceTicksSign
        onChange={(value) => setHighPercentsRaw(value)}
        leftContent={t('trading.sell-high')}
        rightContent={
          <Price>
            <Numeric
              value={highUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </Price>
        }
      />
      <TradingInputSlider
        prefix="-"
        type="sell"
        value={lowPercents.toNumber()}
        ticks={[-100, -75, -50, -25, 0]}
        onChange={(value) => setLowPercentsRaw(value)}
        leftContent={t('trading.sell-low')}
        rightContent={
          <Price>
            <Numeric
              value={lowUsd}
              preset="currency-amount-short"
              currency={'$'}
            />
          </Price>
        }
      />
    </Root>
  )
}

const Root = styled.div`
  background: var(--appearanceA-2);

  display: grid;

  gap: 8px;
  overflow: hidden;
  align-items: center;

  padding: 12px 10px 30px;
  margin-top: 10px;
  gap: 10px;
`

const Note = styled.div`
  color: var(--text-11);

  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  padding-inline: 12px 14px;
`

const Price = styled.div`
  color: var(--text-11);
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  height: 100%;
  display: grid;
  align-items: center;

  position: absolute;
  top: 2px;
  right: 10px;
`
